import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import { avatarUrl } from '@/lib/avatar-url';
import { cn } from '@/lib/utils';

type Role = 'EMPLOYEE' | 'MANAGER' | 'HR_ADMIN' | 'SUPER_ADMIN';
type AbsenceStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
type HalfDayPart = 'AM' | 'PM';

interface CalMember {
  id: string;
  fullName: string;
  email: string;
  role: Role;
  profileColor: string | null;
  avatarUpdatedAt: string | null;
}

interface CalAbsence {
  id: string;
  userId: string;
  absenceTypeId: string;
  startDate: string;
  endDate: string;
  isHalfDay: boolean;
  halfDayPart: HalfDayPart | null;
  status: AbsenceStatus;
  comment: string | null;
}

interface CalType {
  id: string;
  code: string;
  name: string;
  color: string;
}

interface CalHoliday {
  date: string;
  name: string;
}

interface CalResponse {
  from: string;
  to: string;
  members: CalMember[];
  absences: CalAbsence[];
  absenceTypes: CalType[];
  holidays: CalHoliday[];
}

const MONTH_SHORT_ES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
const MONTH_LONG_ES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

export function TeamCalendar() {
  const today = useMemo(() => startOfTodayLocal(), []);
  const [cursor, setCursor] = useState<{ year: number; month: number }>(() => ({
    year: today.getFullYear(),
    month: today.getMonth(),
  }));

  const range = useMemo(() => monthRange(cursor.year, cursor.month), [cursor]);

  const calQuery = useQuery({
    queryKey: ['team', 'calendar', range.from, range.to],
    queryFn: () => api<CalResponse>(`/team/calendar?from=${range.from}&to=${range.to}`),
  });

  const data = calQuery.data;
  const days = useMemo(() => buildDayList(range.from, range.to), [range]);

  const typeById = useMemo(() => {
    const m = new Map<string, CalType>();
    for (const t of data?.absenceTypes ?? []) m.set(t.id, t);
    return m;
  }, [data]);

  const holidayByYmd = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of data?.holidays ?? []) m.set(h.date, h.name);
    return m;
  }, [data]);

  /** Map<userId, Map<ymd, CalAbsence[]>> */
  const absencesByUserDay = useMemo(() => {
    const all = new Map<string, Map<string, CalAbsence[]>>();
    for (const a of data?.absences ?? []) {
      let perDay = all.get(a.userId);
      if (!perDay) {
        perDay = new Map();
        all.set(a.userId, perDay);
      }
      for (const ymdStr of expandRange(a.startDate, a.endDate)) {
        const list = perDay.get(ymdStr) ?? [];
        list.push(a);
        perDay.set(ymdStr, list);
      }
    }
    return all;
  }, [data]);

  function shiftMonth(delta: number) {
    setCursor((c) => {
      const next = new Date(Date.UTC(c.year, c.month + delta, 1));
      return { year: next.getUTCFullYear(), month: next.getUTCMonth() };
    });
  }

  function goToday() {
    setCursor({ year: today.getFullYear(), month: today.getMonth() });
  }

  const monthLong = MONTH_LONG_ES[cursor.month];
  const monthShort = MONTH_SHORT_ES[cursor.month];
  const todayYmdStr = ymd(today);
  const isCurrentMonth = today.getFullYear() === cursor.year && today.getMonth() === cursor.month;

  return (
    <div className="p-6 md:p-8">
      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
        {/* HEADER */}
        <header className="px-7 py-5 border-b border-border flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold tracking-tightish capitalize">
              {monthLong} {cursor.year}
              {data ? (
                <span className="text-muted-foreground font-normal"> · {data.members.length} personas</span>
              ) : null}
            </h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              Quién está dentro o fuera cada día. Pasa el ratón por una celda para el detalle.
            </p>
          </div>
          <div className="inline-flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => shiftMonth(-1)} aria-label="Mes anterior">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span
              className="font-mono text-[13px] font-medium uppercase text-muted-foreground px-3 min-w-[120px] text-center"
              style={{ letterSpacing: '0.1em' }}
            >
              {monthShort} {cursor.year}
            </span>
            <Button variant="ghost" size="icon" onClick={() => shiftMonth(1)} aria-label="Mes siguiente">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <span className="w-px h-6 bg-border mx-1" aria-hidden />
            <Button variant="outline" size="sm" onClick={goToday} disabled={isCurrentMonth}>
              Hoy
            </Button>
          </div>
        </header>

        {/* LEGEND */}
        {data ? (
          <div className="px-7 py-3 border-b border-border flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 flex-wrap">
              {data.absenceTypes.map((t) => (
                <LegendItem key={t.id} color={t.color} label={t.name} />
              ))}
              <LegendItem
                background="rgb(255 244 224)"
                border="rgb(232 197 122)"
                label="Festivo"
              />
            </div>
            <div className="font-mono text-[12px] text-muted-foreground">
              {today.getUTCDate()} {monthLong}
              {isCurrentMonth ? <span className="text-icaria-600 font-medium"> · hoy</span> : null}
            </div>
          </div>
        ) : null}

        {/* GRID */}
        {calQuery.isLoading ? (
          <p className="px-7 py-10 text-center text-sm text-muted-foreground">Cargando…</p>
        ) : calQuery.isError ? (
          <p role="alert" className="px-7 py-10 text-center text-sm text-destructive">
            {calQuery.error instanceof ApiError
              ? calQuery.error.message
              : 'Error al cargar el calendario'}
          </p>
        ) : !data || data.members.length === 0 ? (
          <p className="px-7 py-10 text-center text-sm text-muted-foreground">
            No tienes empleados asignados.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th
                      className="sticky left-0 z-10 bg-paper text-left px-5 py-3 border-b border-r border-border font-mono text-[10px] uppercase text-muted-foreground font-medium"
                      style={{ minWidth: 200, letterSpacing: '0.14em' }}
                    >
                      Equipo
                    </th>
                    {days.map((d) => (
                      <DayHeader
                        key={d.ymd}
                        d={d}
                        isToday={d.ymd === todayYmdStr}
                        holiday={Boolean(holidayByYmd.get(d.ymd))}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.members.map((m) => (
                    <tr key={m.id}>
                      <td
                        className="sticky left-0 z-10 bg-card px-5 py-3 border-b border-r border-border"
                        style={{ minWidth: 200 }}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar
                            fullName={m.fullName}
                            color={m.profileColor}
                            avatarUrl={avatarUrl(m.id, m.avatarUpdatedAt)}
                            previewOnHover
                          />
                          <div className="min-w-0">
                            <div className="text-[13px] font-semibold leading-tight truncate">
                              {m.fullName}
                            </div>
                            <div className="text-[11px] text-muted-foreground leading-tight truncate">
                              {roleLabel(m.role)}
                            </div>
                          </div>
                        </div>
                      </td>
                      {days.map((d) => (
                        <DayCell
                          key={d.ymd}
                          day={d}
                          isToday={d.ymd === todayYmdStr}
                          holidayName={holidayByYmd.get(d.ymd)}
                          absences={absencesByUserDay.get(m.id)?.get(d.ymd) ?? []}
                          typeById={typeById}
                          memberName={m.fullName}
                        />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <FooterBar
              members={data.members}
              today={today}
              isCurrentMonth={isCurrentMonth}
              days={days}
              absencesByUserDay={absencesByUserDay}
              typeById={typeById}
              holidayByYmd={holidayByYmd}
            />
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// PIECES
// =============================================================================

function LegendItem({
  color,
  background,
  border,
  label,
}: {
  color?: string;
  background?: string;
  border?: string;
  label: string;
}) {
  return (
    <div className="inline-flex items-center gap-2 text-[12px] text-foreground">
      <span
        className="inline-block h-2.5 w-2.5 rounded-[2px]"
        style={{
          backgroundColor: color ?? background,
          border: border ? `1px solid ${border}` : undefined,
        }}
        aria-hidden
      />
      <span>{label}</span>
    </div>
  );
}

function DayHeader({ d, isToday, holiday }: { d: DayInfo; isToday: boolean; holiday: boolean }) {
  const isWeekend = d.weekday === 0 || d.weekday === 6;
  return (
    <th
      className={cn(
        'border-b border-r border-border text-center font-mono px-0',
        !isToday && isWeekend && 'bg-paper-warm',
        !isToday && !isWeekend && holiday && 'bg-warning/10',
        isToday && 'bg-icaria-600 text-paper',
      )}
      style={{ minWidth: 32 }}
    >
      <div className={cn('text-[9px] uppercase font-medium pt-1.5', !isToday && 'opacity-60')}>
        {SHORT_WEEKDAY[d.weekday]}
      </div>
      <div className="text-[12px] font-semibold pb-1.5 tabular-nums">
        {String(d.dayOfMonth).padStart(2, '0')}
      </div>
    </th>
  );
}

function DayCell({
  day,
  isToday,
  holidayName,
  absences,
  typeById,
  memberName,
}: {
  day: DayInfo;
  isToday: boolean;
  holidayName: string | undefined;
  absences: CalAbsence[];
  typeById: Map<string, CalType>;
  memberName: string;
}) {
  const isWeekend = day.weekday === 0 || day.weekday === 6;
  const tooltip = buildTooltip(memberName, day, absences, typeById, holidayName);

  const primary =
    absences.find((a) => a.status === 'APPROVED') ?? absences[0] ?? null;
  const type = primary ? typeById.get(primary.absenceTypeId) : null;
  const color = type?.color ?? '#1B23B8';
  const isPending = primary?.status === 'PENDING';
  const isHalf = Boolean(primary?.isHalfDay);
  const halfPart = primary?.halfDayPart;

  const todayShadow = isToday ? 'inset 2px 0 0 0 #1B23B8, inset -2px 0 0 0 #1B23B8' : undefined;

  return (
    <td
      className={cn(
        'border-b border-r border-border p-0 align-top relative',
        !primary && isWeekend && 'bg-paper-warm',
        !primary && !isWeekend && holidayName && 'bg-warning/10',
      )}
      style={{ minWidth: 32, height: 40, boxShadow: todayShadow }}
      title={tooltip}
    >
      {primary ? (
        isHalf ? (
          <div
            className={cn('absolute inset-x-0', halfPart === 'AM' ? 'top-0 h-1/2' : 'bottom-0 h-1/2')}
            style={{
              backgroundColor: color,
              backgroundImage: isPending
                ? 'repeating-linear-gradient(45deg, rgba(255,255,255,0.4) 0 3px, transparent 3px 6px)'
                : undefined,
              opacity: isPending ? 0.7 : 1,
            }}
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{
              backgroundColor: color,
              backgroundImage: isPending
                ? 'repeating-linear-gradient(45deg, rgba(255,255,255,0.4) 0 3px, transparent 3px 6px)'
                : undefined,
              opacity: isPending ? 0.7 : 1,
            }}
          />
        )
      ) : null}
    </td>
  );
}

function FooterBar({
  members,
  today,
  isCurrentMonth,
  days,
  absencesByUserDay,
  typeById,
  holidayByYmd,
}: {
  members: CalMember[];
  today: Date;
  isCurrentMonth: boolean;
  days: DayInfo[];
  absencesByUserDay: Map<string, Map<string, CalAbsence[]>>;
  typeById: Map<string, CalType>;
  holidayByYmd: Map<string, string>;
}) {
  if (!isCurrentMonth) return null;

  const todayYmdStr = ymd(today);
  const todayWeekday = today.getUTCDay();
  const todayHoliday = holidayByYmd.get(todayYmdStr);
  const isOffday = todayWeekday === 0 || todayWeekday === 6 || Boolean(todayHoliday);

  const referenceYmd = isOffday ? findNextWorkdayYmd(today, days, holidayByYmd) : todayYmdStr;
  const referenceDate = referenceYmd ? new Date(`${referenceYmd}T00:00:00.000Z`) : today;

  const peopleOut = members.filter((m) => {
    const list = absencesByUserDay.get(m.id)?.get(referenceYmd ?? '');
    return list && list.length > 0;
  });

  const todayWeekdayName = capitalize(
    today.toLocaleDateString('es-ES', { weekday: 'long', timeZone: 'UTC' }),
  );
  const refWeekdayName = capitalize(
    referenceDate.toLocaleDateString('es-ES', { weekday: 'long', timeZone: 'UTC' }),
  );

  let intro: string;
  if (!isOffday) {
    intro = peopleOut.length > 0 ? '' : 'Hoy todo el equipo está dentro.';
  } else if (todayHoliday) {
    intro = `${todayWeekdayName} · festivo (${todayHoliday}). Próximo ${refWeekdayName} ${referenceDate.getUTCDate()}:`;
  } else {
    intro = `${todayWeekdayName} · fin de semana, equipo no laboral. Próximo ${refWeekdayName} ${referenceDate.getUTCDate()}:`;
  }

  return (
    <div className="px-7 py-3.5 border-t border-border bg-paper flex items-center gap-4 flex-wrap">
      <span
        className="font-mono text-[10px] font-medium uppercase text-muted-foreground"
        style={{ letterSpacing: '0.14em' }}
      >
        Hoy fuera
      </span>
      {intro ? <span className="text-[13px] text-ink-2">{intro}</span> : null}
      <div className="flex items-center gap-2 flex-wrap">
        {peopleOut.length === 0 && !isOffday ? (
          <span className="text-[13px] text-muted-foreground">—</span>
        ) : (
          peopleOut.map((m) => {
            const abs = absencesByUserDay.get(m.id)?.get(referenceYmd ?? '')?.[0];
            const type = abs ? typeById.get(abs.absenceTypeId) : null;
            return (
              <PersonTag
                key={m.id}
                member={m}
                code={type?.code.slice(0, 4).toUpperCase() ?? '?'}
                color={type?.color ?? '#1B23B8'}
              />
            );
          })
        )}
      </div>
    </div>
  );
}

function PersonTag({ member, code, color }: { member: CalMember; code: string; color: string }) {
  return (
    <span className="inline-flex items-center gap-2 pl-0.5 pr-2.5 py-0.5 rounded-full border border-line-strong bg-card">
      <Avatar
        fullName={member.fullName}
        color={member.profileColor}
        avatarUrl={avatarUrl(member.id, member.avatarUpdatedAt)}
        size="xs"
        previewOnHover
      />
      <span className="text-[12px] font-medium leading-tight">{member.fullName}</span>
      <span
        className="font-mono text-[9px] font-semibold uppercase tracking-cap"
        style={{ color }}
      >
        {code}
      </span>
    </span>
  );
}

// =============================================================================
// HELPERS
// =============================================================================

interface DayInfo {
  ymd: string;
  weekday: number;
  dayOfMonth: number;
}

const SHORT_WEEKDAY = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];

function roleLabel(role: Role): string {
  switch (role) {
    case 'EMPLOYEE':
      return 'Empleado';
    case 'MANAGER':
      return 'Manager';
    case 'HR_ADMIN':
      return 'RRHH';
    case 'SUPER_ADMIN':
      return 'Admin';
  }
}

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function monthRange(year: number, month: number): { from: string; to: string } {
  const first = new Date(Date.UTC(year, month, 1));
  const last = new Date(Date.UTC(year, month + 1, 0));
  return { from: ymd(first), to: ymd(last) };
}

function buildDayList(fromYmd: string, toYmd: string): DayInfo[] {
  const out: DayInfo[] = [];
  const start = new Date(`${fromYmd}T00:00:00.000Z`);
  const end = new Date(`${toYmd}T00:00:00.000Z`);
  for (let d = new Date(start); d.getTime() <= end.getTime(); d.setUTCDate(d.getUTCDate() + 1)) {
    out.push({
      ymd: ymd(d),
      weekday: d.getUTCDay(),
      dayOfMonth: d.getUTCDate(),
    });
  }
  return out;
}

function expandRange(fromYmd: string, toYmd: string): string[] {
  const out: string[] = [];
  const start = new Date(`${fromYmd}T00:00:00.000Z`);
  const end = new Date(`${toYmd}T00:00:00.000Z`);
  for (let d = new Date(start); d.getTime() <= end.getTime(); d.setUTCDate(d.getUTCDate() + 1)) {
    out.push(ymd(d));
  }
  return out;
}

function ymd(d: Date): string {
  const y = d.getUTCFullYear();
  const m = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = d.getUTCDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfTodayLocal(): Date {
  const n = new Date();
  return new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate()));
}

function findNextWorkdayYmd(
  from: Date,
  days: DayInfo[],
  holidayByYmd: Map<string, string>,
): string | null {
  const fromYmd = ymd(from);
  for (const d of days) {
    if (d.ymd <= fromYmd) continue;
    const isWeekend = d.weekday === 0 || d.weekday === 6;
    if (!isWeekend && !holidayByYmd.get(d.ymd)) return d.ymd;
  }
  return null;
}

function buildTooltip(
  memberName: string,
  day: DayInfo,
  absences: CalAbsence[],
  typeById: Map<string, CalType>,
  holidayName: string | undefined,
): string {
  const parts: string[] = [`${memberName} — ${day.ymd}`];
  if (holidayName) parts.push(`Festivo: ${holidayName}`);
  for (const a of absences) {
    const t = typeById.get(a.absenceTypeId);
    const status = a.status === 'APPROVED' ? 'Aprobada' : 'Pendiente';
    const range = a.startDate === a.endDate ? a.startDate : `${a.startDate} → ${a.endDate}`;
    const half = a.isHalfDay ? ` (½ ${a.halfDayPart})` : '';
    const comment = a.comment ? ` — ${a.comment}` : '';
    parts.push(`${t?.name ?? 'Ausencia'} · ${status}${half} · ${range}${comment}`);
  }
  return parts.join('\n');
}
